/**
 * @file RolesManager.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-27
 * @modified 2026-10-15
 *
 * The Roles tab of User Management: every role, what badge it confers, how many
 * permissions it carries and how many people hold it.
 *
 * Built-in roles render with the same action buttons as custom ones, disabled
 * with a reason rather than omitted — the project's rule for table actions, and
 * it matters here because a ragged row would read as the role being different
 * in kind rather than simply protected from deletion.
 */

import React from 'react';
import './TableStyles.css';
import { useSlideOutButton } from './SlideOutButton';

function RolesManager({ roles, onEdit, onDelete, onCreate, busyRoleId }) {
  // The Roles tab mounts with its New Role button: measure it here.
  useSlideOutButton([roles.length]);
  return (
    <div className="roles-manager">
      <div className="section-header">
        <h2>Roles ({roles.length})</h2>
        <div className="buttons slideout">
          <div>
            <button className="btn-slideout" onClick={onCreate}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">add</span></div>
              <span className="btn-label">New Role</span>
            </button>
          </div>
        </div>
      </div>

      <p className="section-blurb">
        A role is a named group of permissions. A user can hold several, and their
        access is the total of all of them — then adjusted per person on the
        Users tab. The badge a role carries decides two things no permission can:
        whether the account is view-only, and whether it uses Xero.
      </p>

      <div className="table-container">
        <table className="users-table roles-table">
          <thead>
            <tr>
              <th>Role</th>
              <th><div className="centred">Badge</div></th>
              <th><div className="centred">Xero</div></th>
              <th><div className="centred">Permissions</div></th>
              <th><div className="centred">Users</div></th>
              <th className="actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {roles.map(role => {
              const systemLocked = role.is_system === true;
              const inUse = role.user_count > 0;
              return (
                <tr key={role.id}>
                  <td>
                    <strong>{role.name}</strong>
                    {role.description && <div className="role-description">{role.description}</div>}
                  </td>
                  <td>
                    <div className="centred">
                      <span className={`status-badge badge-${role.badge.toLowerCase()}`}>
                        {role.badge === 'ADMIN' ? 'Admin' : role.badge === 'REP' ? 'Rep' : 'User'}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="centred">
                      {role.badge === 'REP' ? (
                        <span className="muted" title="Rep accounts cannot connect to Xero">Not used</span>
                      ) : role.requires_xero ? (
                        <span title="Holders are blocked until Xero is connected">Required</span>
                      ) : (
                        <span className="muted">Optional</span>
                      )}
                    </div>
                  </td>
                  <td><div className="centred">{(role.permissions || []).length}</div></td>
                  <td><div className="centred">{role.user_count}</div></td>
                  <td className="actions-col">
                    <button
                      className="btn-icon"
                      onClick={() => onEdit(role)}
                      disabled={busyRoleId === role.id}
                      title="Edit role"
                    >
                      <span className="material-symbols-rounded">edit</span>
                    </button>
                    <button
                      className={`btn-icon btn-danger${systemLocked || inUse ? ' btn-disabled' : ''}`}
                      onClick={() => onDelete(role)}
                      disabled={systemLocked || inUse || busyRoleId === role.id}
                      title={
                        systemLocked
                          ? 'Built-in roles cannot be deleted'
                          : inUse
                            ? `Assigned to ${role.user_count} user${role.user_count === 1 ? '' : 's'} — remove it from them first`
                            : 'Delete role'
                      }
                    >
                      <span className="material-symbols-rounded">delete</span>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default RolesManager;
